/**
 * Verificación de la CIEC tras el envío (contrato C3): el front consulta verification-status cada
 * 3 s durante 90 s como mucho y traduce el estado al mensaje que ve el cliente.
 *
 * Lógica pura (sin React) para poder probarla; el hook está en useVerificationStatus.ts.
 */

import { getOnboardingApiUrl } from "./actions";

export const VERIFICATION_POLL = {
  /** cada cuánto se consulta */
  INTERVAL_MS: 3_000,
  /** a partir de aquí se deja de consultar y se muestra el mensaje neutro */
  TIMEOUT_MS: 90_000,
} as const;

// estados que devuelve verification-status (C3)
export type VerificationApiStatus = "pending" | "verified" | "rejected" | "unavailable";
const API_STATUSES: readonly string[] = ["pending", "verified", "rejected", "unavailable"];

// resultado de una consulta: el estado, "retry" (fallo pasajero: se vuelve a consultar) o "stop"
// (no hay verificación que consultar: no tiene sentido insistir)
export type VerificationPoll =
  | { status: VerificationApiStatus; reason: string | null }
  | "retry"
  | "stop";

// lo que ve el cliente al terminar; "continues" = la verificación sigue, le contactaremos si hace falta
export type VerificationOutcome = "verified" | "credentials_rejected" | "account_locked" | "continues";

// lo que muestra la pantalla: mientras se consulta, "checking"
export type VerificationView = "checking" | VerificationOutcome;

/**
 * Resultado de una consulta; null = seguir consultando.
 */
export function outcomeOf(poll: VerificationPoll): VerificationOutcome | null {
  if (poll === "stop") return "continues";
  if (poll === "retry") return null;
  switch (poll.status) {
    case "verified":
      return "verified";
    case "rejected":
      if (poll.reason === "credentials_rejected") return "credentials_rejected";
      if (poll.reason === "account_locked") return "account_locked";
      return "continues";
    case "unavailable":
      return "continues";
    case "pending":
      return null;
  }
}

export interface PollOptions {
  now: () => number;
  sleep: (ms: number) => Promise<void>;
  isCancelled?: () => boolean;
  intervalMs?: number;
  timeoutMs?: number;
}

/**
 * Consulta cada intervalMs hasta tener un resultado o agotar timeoutMs ("continues").
 * Si se cancela (la pantalla se desmonta), deja de consultar.
 */
export async function pollVerification(
  fetchOnce: () => Promise<VerificationPoll>,
  {
    now,
    sleep,
    isCancelled = () => false,
    intervalMs = VERIFICATION_POLL.INTERVAL_MS,
    timeoutMs = VERIFICATION_POLL.TIMEOUT_MS,
  }: PollOptions
): Promise<VerificationOutcome> {
  const deadline = now() + timeoutMs;
  while (!isCancelled()) {
    // george-sat tarda unos segundos en hacer el login: se espera antes de cada consulta
    await sleep(intervalMs);
    if (isCancelled()) break;
    const outcome = outcomeOf(await fetchOnce());
    if (outcome) return outcome;
    if (now() >= deadline) break;
  }
  return "continues";
}

/**
 * Una consulta a verification-status con el sessionId de la sesión del onboarding.
 */
export async function fetchVerificationStatus(sessionId: string): Promise<VerificationPoll> {
  try {
    const response = await fetch(`${getOnboardingApiUrl()}/api/onboarding/verification-status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId }),
    });
    if (!response.ok) {
      // 4xx: sesión caducada o sin conexión, no cambiará; 5xx: pasajero
      return response.status >= 500 ? "retry" : "stop";
    }
    const body = await response.json();
    if (!body || !API_STATUSES.includes(body.status)) return "retry";
    return {
      status: body.status as VerificationApiStatus,
      reason: typeof body.reason === "string" ? body.reason : null,
    };
  } catch {
    // red caída o respuesta ilegible: se vuelve a intentar en la siguiente vuelta
    return "retry";
  }
}
